import axios from 'axios';

// ──────────────────────────────────────────────────────────────
//  Allergens store
//
//  The server returns each allergen with all language columns
//  (allergenName, allergenName_da, allergenName_sv, allergenName_no,
//  allergenName_fi). On the client we pick the column matching the user's
//  language and surface it under the `allergenName` key, so any consumer
//  (chips, lookup helpers, the print pipeline) sees the right language
//  without needing to know about the alternates.
//
//  The list endpoint returns the raw rows; we map them on the way in so
//  state.allergens always carries the localised name.
// ──────────────────────────────────────────────────────────────

const state = {
  allergens: [],
  loading: false,
  error: null,
};

const getters = {
  allergenById: (state) => (id) => state.allergens.find(a => a.allergenId === id),
};

const mutations = {
  SET_ALLERGENS(state, allergens) { state.allergens = allergens; },
  SET_LOADING(state, loading)     { state.loading = loading; },
  SET_ERROR(state, error)         { state.error = error; },
};

// Resolve the user's language the same way SettingsPage does, so the names
// in the chip list match what the user sees elsewhere in the app.
function getCurrentLanguage() {
  const userId = localStorage.getItem('userId') || 'default_user';
  let language = localStorage.getItem(`${userId}_language`);
  if (!language) {
    const browserLang = navigator.language || navigator.userLanguage || '';
    language = browserLang.split('-')[0];
  }
  const supported = ['da', 'sv', 'no', 'fi', 'en'];
  if (!supported.includes(language)) language = 'da';
  return language;
}

// Pull the localised name out of a raw allergen row, falling back to the
// English allergenName when the localised column is empty.
function localiseAllergen(row, lang) {
  if (!row) return row;
  const localised = row[`allergenName_${lang}`];
  return {
    ...row,
    allergenName: localised || row.allergenName,
  };
}

const actions = {
  async fetchAllergens({ commit }) {
    const baseURL  = process.env.VUE_APP_API_URL || 'http://127.0.0.1/api';
    const token    = localStorage.getItem('userToken');
    const lang     = getCurrentLanguage();

    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    try {
      const res  = await axios.get(`${baseURL}/allergens`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = Array.isArray(res.data) ? res.data : [];
      commit('SET_ALLERGENS', list.map(r => localiseAllergen(r, lang)));
    } catch (error) {
      console.error('Error fetching allergens:', error);
      commit('SET_ERROR', error.message);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // Fetch the allergens linked to a specific food item. Uses the foodItems
  // detail endpoint (which already returns the linked allergens plus all
  // locale columns) — no separate /allergens/item/:id endpoint needed.
  async getFoodItemAllergens(_ctx, itemId) {
    const baseURL = process.env.VUE_APP_API_URL || 'http://127.0.0.1/api';
    const token   = localStorage.getItem('userToken');
    const lang    = getCurrentLanguage();

    try {
      const res  = await axios.get(`${baseURL}/fooditems/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = Array.isArray(res.data.allergens) ? res.data.allergens : [];
      return list.map(r => localiseAllergen(r, lang));
    } catch (error) {
      console.error('Error fetching item allergens:', error);
      throw error;
    }
  },

  // Save the allergens linked to a food item. The foodItems PUT endpoint
  // accepts an allergenIds array directly, so we just send a PUT with that
  // single field — no separate allergen endpoint.
  async setFoodItemAllergens(_ctx, { itemId, allergenIds }) {
    const baseURL = process.env.VUE_APP_API_URL || 'http://127.0.0.1/api';
    const token   = localStorage.getItem('userToken');

    try {
      const res = await axios.put(
        `${baseURL}/fooditems/${itemId}`,
        { allergenIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return res.data;
    } catch (error) {
      console.error('Error setting item allergens:', error);
      throw error;
    }
  },

  // Kept under the legacy name so existing callers (ItemFormDialog's
  // updateFoodItemAllergens action) keep working.
  async updateFoodItemAllergens(ctx, payload) {
    return ctx.dispatch('setFoodItemAllergens', payload);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};